import Component from "@glimmer/component";
import { pageTitle } from "ember-page-title";
import PresetSelector from "#components/preset-selector.gts";
import EventLog, { EventLogger } from "#components/sandbox/event-log.gts";
import { PRESETS, type PresetDefinition } from "#models/data/presets.ts";
import { deserializeSong } from "#utils/serialization.ts";
import type { Song } from "#models/song.ts";
import type { Sound } from "#models/sound.ts";

interface ChannelSummary {
  sound: Sound;
  activeStepsText: string;
  totalSteps: number;
}

interface PresetItem {
  id: string;
  displayName: string;
  rawData: string;
  song?: Song;
  error?: string;
  channelSummaries?: ChannelSummary[];
}

export default class PresetsTemplateComponent extends Component {
  logger = new EventLogger();

  handleSelectPreset = (song: Song): void => {
    this.logger.log(
      `Selected preset: "${song.name}" (tempo: ${song.tempo} BPM, channels: ${song.channels.length})`,
    );
  };

  get decodedPresets(): PresetItem[] {
    return PRESETS.map((presetDef: PresetDefinition) => {
      try {
        const song = deserializeSong(presetDef.data);
        const channelSummaries: ChannelSummary[] = song.channels.map((channel) => {
          const activeSteps: string[] = [];
          channel.steps.forEach((step, index) => {
            if (step.enabled) {
              if (step.velocity === 0.5) {
                activeSteps.push(`${index}*`);
              } else if (step.velocity !== 1.0) {
                activeSteps.push(`${index} (${step.velocity})`);
              } else {
                activeSteps.push(`${index}`);
              }
            }
          });
          return {
            sound: channel.sound,
            activeStepsText: activeSteps.length > 0 ? activeSteps.join(", ") : "none",
            totalSteps: channel.steps.length,
          };
        });

        return {
          id: presetDef.id,
          displayName: presetDef.name,
          rawData: presetDef.data,
          song,
          channelSummaries,
        };
      } catch (err) {
        return {
          id: presetDef.id,
          displayName: presetDef.name,
          rawData: presetDef.data,
          error: err instanceof Error ? err.message : String(err),
        };
      }
    });
  }

  <template>
    {{pageTitle "Presets"}}
    <div class="space-y-8 text-sm" data-test-presets-sandbox>
      {{! Interactive PresetSelector Demo }}
      <section class="bg-slate-900/80 space-y-4">
        <div>
          <h2 class="text-xl font-bold mb-1">Preset Selector Component</h2>
          <p class="text-xs text-slate-400">Interactive preview of the PresetSelector component.</p>
        </div>

        <div>
          <PresetSelector @onSelect={{this.handleSelectPreset}} />
        </div>

        <EventLog @logger={{this.logger}} data-test-sandbox-presets-event-log />
      </section>

      {{! Deserialization Reference List }}
      <section>
        <h2 class="text-xl font-bold mb-1">Presets Serialization Test</h2>
        <p class="text-xs text-gray-500 mb-4">* Asterisk indicates half-velocity (0.5 accent) step.</p>
        <ul class="space-y-4">
          {{#each this.decodedPresets as |preset|}}
            <li class="border-b border-gray-300 pb-3">
              <div><span class="text-yellow-500 font-semibold">Preset ID:</span>
                <span class="font-mono">{{preset.id}}</span></div>
              <div><span class="text-yellow-500 font-semibold">Display Name:</span>
                {{preset.displayName}}</div>
              <div class="wrap-break-word font-mono text-xs text-gray-500 my-1 max-w-full">
                <span class="text-yellow-500 font-thin">Raw Data:</span>
                {{preset.rawData}}
              </div>
              {{#if preset.error}}
                <div class="text-red-600"><span class="text-yellow-500 font-thin">Error:</span>
                  {{preset.error}}</div>
              {{else}}
                <div class="font-mono text-gray-600 pl-2">
                  <div><span class="text-yellow-500 font-thin">Song Name:</span>
                    {{preset.song.name}}</div>
                  <div><span class="text-yellow-500 font-thin">Tempo:</span>
                    {{preset.song.tempo}}
                    BPM</div>
                  <div><span class="text-yellow-500 font-thin">Channels ({{preset.song.channels.length}}):</span></div>
                  <ul class="ml-4 list-disc space-y-1 mt-1">
                    {{#each preset.channelSummaries as |ch|}}
                      <li>
                        <span class="text-yellow-500 font-thin">{{ch.sound.name}}</span>: active
                        steps [{{ch.activeStepsText}}] ({{ch.totalSteps}}
                        steps total)
                      </li>
                    {{else}}
                      <li>No channels</li>
                    {{/each}}
                  </ul>
                </div>
              {{/if}}
            </li>
          {{/each}}
        </ul>
      </section>
    </div>
  </template>
}
